import { CSSProperties } from 'react';

// Types
import type { AnimationState } from '@/types';

/**
 * Common animation style generators
 */
export const createAnimationStyles = {
  /**
   * Create pop-in animation (scale + opacity)
   */
  popIn: (anim: AnimationState): CSSProperties => ({
    transform: `scale(${anim.scale})`,
    opacity: anim.opacity,
  }),

  /**
   * Slide from left with fade (professional entrance)
   */
  slideFromLeft: (anim: AnimationState): CSSProperties => ({
    transform: `translateX(${-50 * (1 - anim.opacity)}px) scale(${anim.scale})`,
    opacity: anim.opacity,
  }),

  /**
   * Slide from right with fade
   */
  slideFromRight: (anim: AnimationState): CSSProperties => ({
    transform: `translateX(${50 * (1 - anim.opacity)}px) scale(${anim.scale})`,
    opacity: anim.opacity,
  }),

  /**
   * Slide from bottom with bounce (exciting entrance)
   */
  slideFromBottom: (anim: AnimationState): CSSProperties => ({
    transform: `translateY(${30 * (1 - anim.opacity)}px) scale(${anim.scale})`,
    opacity: anim.opacity,
  }),

  /**
   * Dramatic scale with subtle rotation
   */
  dramaticEntrance: (anim: AnimationState): CSSProperties => ({
    transform: `scale(${anim.scale}) rotate(${2 * (1 - anim.opacity)}deg)`,
    opacity: anim.opacity,
  }),

  /**
   * Character reveal - slide up and fade in (for letter-by-letter animations)
   */
  characterReveal: (anim: AnimationState): CSSProperties => ({
    transform: `translateY(${20 * (1 - anim.opacity)}px)`,
    opacity: anim.opacity,
    display: 'inline-block',
  }),
};

// Cross-browser clip-path helper
export const createClipPathStyle = (clipPath: string): CSSProperties => ({
  clipPath,
  WebkitClipPath: clipPath,
});

// Hardware-accelerated backface visibility
export const createBackfaceStyle = (): CSSProperties => ({
  backfaceVisibility: 'hidden',
  WebkitBackfaceVisibility: 'hidden',
});

/**
 * Converts hex color with opacity percentage to rgba
 */
export const hexToRgba = (hex: string, opacity: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
};
